import { useContext, forwardRef, useState, useRef } from 'react';
import type { KeyboardEvent, PropsWithoutRef } from 'react';

import { Icon, useConsolidatedRef, useI18n } from '@pega/cosmos-react-core';
import { getXMLAttributes, RichTextEditor } from '@pega/cosmos-react-rte';
import type { RichTextEditorState } from '@pega/cosmos-react-rte';

import { MentionButtonConfig } from '../MentionButton';
import { HashtagButtonConfig } from '../HashtagButton';
import { AutopilotConfig } from '../Autopilot';

import { FeedContext } from './Feed.context';
import type { FeedRichTextProps, SearchType } from './Feed.types';
import MarkdownSyntax from './MarkdownSyntax';

export const minInputHeight = '4rem';

const FeedRichText = forwardRef<RichTextEditorState, PropsWithoutRef<FeedRichTextProps>>(
  function FeedRichText(props, ref) {
    const editorRef = useConsolidatedRef(ref);
    const { defaultValue, height = { min: minInputHeight }, onKeyDown, ...restProps } = props;
    const {
      autopilotInfo: { id: autopilotId, label: autopilotLabel },
      searchTypes = [],
      onSearch,
      searchResults = [],
      showSearchResults,
      setShowSearchResults
    } = useContext(FeedContext);
    const [triggerType, setTriggerType] = useState<'mention' | 'tag'>('mention');
    const [showMentionType, setShowMentionType] = useState(false);
    const searchType = useRef('');
    const t = useI18n();

    const onDecoSearch = (event: { search: string; trigger: string }) => {
      if (!onSearch) {
        return;
      }

      const trigger = event.trigger === '@' ? 'mention' : 'tag';
      setTriggerType(trigger);

      if (trigger === 'mention' && event.search === '') {
        setShowMentionType(true);
      } else {
        setShowMentionType(false);
        onSearch({
          type: trigger,
          search: event.search,
          mentionType: searchType.current
        });
      }
    };

    const typesMenu = {
      onItemActive: (id: string) => {
        searchType.current = id;
      },
      onItemClick: (id: string) => {
        searchType.current = id;
        setShowMentionType(false);

        if (id !== autopilotId) {
          onSearch?.({ type: 'mention', search: '', mentionType: id });
        }
      },
      items: searchTypes.map((type: SearchType) => {
        let label = type.label;
        let icon = type.icon ?? type.id;
        if (type.id === autopilotId) {
          label = autopilotLabel;
          icon = 'polaris';
        }
        return {
          id: type.id,
          primary: label,
          visual: icon ? <Icon name={icon} /> : undefined
        };
      })
    };

    const searchMenu =
      searchResults.length && showSearchResults
        ? {
            header: triggerType === 'mention' ? t('mention_type', [searchType.current]) : '',
            items: searchResults.map(res => {
              return { ...res, href: undefined };
            })
          }
        : undefined;

    const getSearchAttributes = (xmlElement: string, searchResult: string) => {
      switch (xmlElement) {
        case 'pega-mention': {
          const attributes = getXMLAttributes(searchResult);

          if (!attributes) return;
          return {
            markdown: searchResult,
            component: MentionButtonConfig.component,
            props: { ...attributes, tabIndex: -1 },
            text: attributes.text
          };
        }
        case 'pega-tag': {
          const attributes = getXMLAttributes(searchResult);

          if (!attributes) return;
          return {
            markdown: searchResult,
            component: HashtagButtonConfig.component,
            props: { ...attributes, tabIndex: -1 },
            text: attributes.tag
          };
        }
        case 'pega-autopilot': {
          const attributes = getXMLAttributes(searchResult);

          if (!attributes) return;
          return {
            markdown: searchResult,
            component: AutopilotConfig.component,
            props: { ...attributes, tabIndex: -1 },
            text: autopilotLabel
          };
        }
        default:
      }
    };

    return (
      <RichTextEditor
        additionalInfo={{
          heading: t('markdown_syntax'),
          content: <MarkdownSyntax />
        }}
        {...restProps}
        defaultValue={defaultValue}
        onKeyDown={(e: KeyboardEvent) => {
          setShowSearchResults?.(true);
          onKeyDown?.(e);
        }}
        markdownOnly
        ref={editorRef}
        height={height}
        searchTriggers={[
          { trigger: '@', regex: String.raw`(?:\S+(?:[^\n\S][^@#\s]\S*)*[^\n\S]?|)` },
          { trigger: '#', regex: String.raw`[^\s,]*` }
        ]}
        getSearchItemReplacement={(id: string) => {
          if (id === autopilotId) {
            return {
              markdown: AutopilotConfig.inject(),
              component: AutopilotConfig.component,
              props: { tabIndex: -1 },
              text: autopilotLabel
            };
          }

          const item = searchResults?.find(({ id: itemId }) => itemId === id);

          if (!item) return undefined;

          return {
            markdown:
              triggerType === 'mention'
                ? MentionButtonConfig.inject({
                    id: item.id,
                    type: searchType.current,
                    text: item.primary,
                    href: item.href
                  })
                : HashtagButtonConfig.inject({ tag: item.id }),
            component:
              triggerType === 'mention'
                ? MentionButtonConfig.component
                : HashtagButtonConfig.component,
            props:
              triggerType === 'mention'
                ? {
                    id: item.id,
                    text: item.primary,
                    type: searchType.current,
                    tabIndex: -1
                  }
                : { tag: item.primary, tabIndex: -1 },
            text: item.primary
          };
        }}
        searchRenderers={[
          {
            type: MentionButtonConfig.type,
            xmlElement: MentionButtonConfig.xmlElement,
            regexPattern: MentionButtonConfig.regexPattern,
            getSearchAttributes
          },
          {
            type: HashtagButtonConfig.type,
            xmlElement: HashtagButtonConfig.xmlElement,
            regexPattern: HashtagButtonConfig.regexPattern,
            getSearchAttributes
          },
          {
            type: AutopilotConfig.type,
            xmlElement: AutopilotConfig.xmlElement,
            regexPattern: AutopilotConfig.regexPattern,
            getSearchAttributes
          }
        ]}
        onSearch={onSearch ? onDecoSearch : undefined}
        menu={showMentionType ? typesMenu : searchMenu}
      />
    );
  }
);

export default FeedRichText;
